import {Input, Label, type PressEvent, TextField} from 'react-aria-components'

import ChevronDownSvg from '@/assets/icons/chevron-down.svg?react'
import InfoSvg from '@/assets/icons/info.svg?react'
import lockSvg from '@/assets/icons/lock.svg'
import PriceHistorySvg from '@/assets/icons/price-history.svg?react'
import stripeTrustBadgePng from '@/assets/images/stripe-trust-badge.png'
import {useIsAuthenticatedValue} from '@/auth/hooks/useIsAuthenticated'
import Price from '@/components/Price'
import PrimaryButton from '@/components/PrimaryButton/PrimaryButton'
import Switch from '@/components/Switch/Switch'
import {USD_DISPLAY_DECIMALS} from '@/constants/config'
import type useFoodCartSummary from '@/lib/food/hooks/useFoodCartSummary'
import {useDropOffNote} from '@/lib/food/store'
import {useRoundUp} from '@/lib/payment/store'
import {FoodPaymentRoute} from '@/routeRegistry'
import {useIsMobile} from '@/utils/hooks/useWindowSize'
import FoodTimeSelector from '@/views/Food/components/FoodTimeSelector/FoodTimeSelector'
import style from '@/views/Food/FoodCart/FoodCart.module.scss'
import FoodExpressCheckout from '@/views/Food/FoodCart/FoodExpressCheckout'

interface Props {
  onSubmit: MemoizedCallback<(e: PressEvent) => void>
  isPending: boolean
  isRewardsApplied: boolean
  setIsRewardsApplied: MemoizedCallbackOrDispatch<boolean>
  cartSummary: ReturnType<typeof useFoodCartSummary>
  coupon?: string
  validateContactInfo: () => Promise<
    | {
        email: string
        name: string
        phone: string
      }
    | undefined
  >
}
export default memo(function FoodPaymentSummary({
  onSubmit,
  isPending,
  isRewardsApplied,
  cartSummary,
  coupon,
  validateContactInfo,
}: Props) {
  const isMobile = useIsMobile()
  const [isOpen, setIsOpen] = useState(!isMobile)
  const isAuthenticated = useIsAuthenticatedValue()
  const {method} = FoodPaymentRoute.useSearch()
  const [roundUp, setRoundUp] = useRoundUp()
  const [dropOffNote, setDropOffNote] = useDropOffNote()

  const {
    cashback,
    cartItemNumber,
    subtotal,
    total,
    tax,
    discountValue,
    derivedDiscountValue,
    isFetching,
    couponInfo,
    totalAfterTax,
    deliveryFee,
    serviceFee,
    smallOrderFee,
    roundUpAmount,
  } = cartSummary

  return (
    <div className={style.CartSummary}>
      <TextField onChange={setDropOffNote} value={dropOffNote} className={style.InputField}>
        <Label>Drop-off notes</Label>
        <Input className={style.Input} placeholder='Add your notes here' />
      </TextField>
      <FoodTimeSelector />
      <h2 className={style.Title}>
        Order summary{' '}
        <button
          className={style.ShowHide}
          aria-label='Order summary'
          aria-expanded={isOpen}
          onClick={() => {
            setIsOpen(isOpen => !isOpen)
          }}
        >
          <ChevronDownSvg className={style.Decoration} aria-hidden />
        </button>
      </h2>
      {isOpen && (
        <>
          <div className={style.SumCategory}>
            <span className={style.SumCategoryName}>
              Subtotal ({cartItemNumber} item{cartItemNumber > 1 && 's'})
            </span>
            <span className={clsx(style.SumCategoryValue, isFetching && style.Loading)}>
              <Price
                price={subtotal}
                options={{
                  fractionDigits: USD_DISPLAY_DECIMALS,
                  exactFractionDigits: true,
                }}
              />
            </span>
          </div>
          <div className={style.SumCategory}>
            <span className={style.SumCategoryName}>Delivery fee</span>
            <span className={clsx(style.SumCategoryValue, isFetching && style.Loading)}>
              <Price
                price={deliveryFee.toNumber()}
                options={{
                  fractionDigits: USD_DISPLAY_DECIMALS,
                  exactFractionDigits: true,
                }}
              />
            </span>
          </div>
          <div className={style.SumCategory}>
            <span className={style.SumCategoryName}>Service fee</span>
            <span className={clsx(style.SumCategoryValue, isFetching && style.Loading)}>
              <Price
                price={serviceFee.toNumber()}
                options={{
                  fractionDigits: USD_DISPLAY_DECIMALS,
                  exactFractionDigits: true,
                }}
              />
            </span>
          </div>
          {smallOrderFee.gt(0) && (
            <div className={style.SumCategory}>
              <span className={style.SumCategoryName}>Small order fee</span>
              <span className={clsx(style.SumCategoryValue, isFetching && style.Loading)}>
                <Price
                  price={smallOrderFee.toNumber()}
                  options={{
                    fractionDigits: USD_DISPLAY_DECIMALS,
                    exactFractionDigits: true,
                  }}
                />
              </span>
            </div>
          )}
          <div className={style.Divider} />
          <div className={style.SumCategory}>
            <span className={clsx(style.SumCategoryName)}>Tax</span>
            <span className={clsx(style.SumCategoryValue, isFetching && style.Loading)}>
              <Price
                price={tax}
                options={{
                  fractionDigits: USD_DISPLAY_DECIMALS,
                  exactFractionDigits: true,
                }}
              />
            </span>
          </div>
          {roundUpAmount.gt(0) && (
            <>
              <div className={style.SumCategory}>
                <span className={clsx(style.SumCategoryName)}>Total after tax</span>
                <span className={clsx(style.SumCategoryValue, isFetching && style.Loading)}>
                  <Price
                    price={totalAfterTax}
                    options={{
                      fractionDigits: USD_DISPLAY_DECIMALS,
                      exactFractionDigits: true,
                    }}
                  />
                </span>
              </div>
              <div className={clsx(style.SumCategory, style.Secondary)}>
                <div className={style.SwitchWrapper}>
                  <Switch
                    label='Donate change to NFB'
                    onChange={setRoundUp}
                    isSelected={roundUp}
                    isLabelVisible
                    labelClassName={style.SumCategoryName}
                  />
                  <a
                    href='https://blog.innosearch.ai/'
                    className={style.Info}
                    target='_blank'
                    rel='noopener noreferrer'
                  >
                    <InfoSvg aria-label='Read more about NFB change donation program' />
                  </a>
                </div>
                <span
                  className={clsx(
                    style.SumCategoryValue,
                    roundUp ? style.RoundedUp : style.UnRounded,
                    isFetching && style.Loading,
                  )}
                >
                  <Price
                    price={roundUpAmount}
                    options={{
                      fractionDigits: USD_DISPLAY_DECIMALS,
                      exactFractionDigits: true,
                    }}
                  />
                </span>
              </div>
            </>
          )}
          {isAuthenticated && isRewardsApplied && discountValue > 0 && (
            <div className={style.SumCategory}>
              <span className={clsx(style.SumCategoryName, style.GoodValue)}>Rewards applied</span>
              <span
                className={clsx(
                  style.SumCategoryValue,
                  derivedDiscountValue > 0 && style.GoodValue,
                  isFetching && style.Loading,
                )}
              >
                <Price
                  price={-derivedDiscountValue}
                  options={{
                    fractionDigits: USD_DISPLAY_DECIMALS,
                    exactFractionDigits: true,
                  }}
                />
              </span>
            </div>
          )}
          {!isRewardsApplied && couponInfo && (
            <div className={style.CouponInfo}>
              <div>Coupon applied</div>
              <div>
                <Price
                  price={-couponInfo.amount}
                  options={{
                    fractionDigits: USD_DISPLAY_DECIMALS,
                    exactFractionDigits: true,
                  }}
                />
              </div>
            </div>
          )}
        </>
      )}
      <div className={style.Total}>
        <span>Total</span>
        <span className={clsx(isFetching && style.Loading)}>
          <Price
            price={total}
            options={{
              fractionDigits: USD_DISPLAY_DECIMALS,
              exactFractionDigits: true,
            }}
          />
        </span>
      </div>
      {isOpen && cashback && (
        <div className={style.SumCategory}>
          <span className={clsx(style.SumCategoryName, style.GoodValue, style.Strong)}>
            <PriceHistorySvg className={style.Decoration} aria-hidden />
            Rewards ({cashback.rate.mul(100).toNumber()}%)
          </span>
          <span
            className={clsx(style.SumCategoryValue, style.GoodValue, isFetching && style.Loading)}
          >
            <Price
              price={cashback.value}
              options={{
                fractionDigits: USD_DISPLAY_DECIMALS,
                exactFractionDigits: true,
              }}
            />
          </span>
        </div>
      )}
      {!method && (
        <FoodExpressCheckout
          applyRewards={isRewardsApplied}
          coupon={coupon}
          validateContactInfo={validateContactInfo}
        />
      )}
      <PrimaryButton
        className={style.PayBtn}
        onPress={onSubmit}
        isDisabled={cartItemNumber === 0 || isFetching}
        isPending={isPending}
        aria-label='Pay with Card (Guaranteed safe & secure, powered by Stripe)'
      >
        {method && !isPending && <img className={style.LockIcon} src={lockSvg} alt='' />}
        {(() => {
          if (!method) return 'Next step'
          if (isPending) return 'Paying...'
          return 'Pay with Card'
        })()}
      </PrimaryButton>
      {isOpen && <img src={stripeTrustBadgePng} alt='' />}
    </div>
  )
})
